import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, ArrowLeft, Gift } from "lucide-react";
import { LOGO_URL } from "@/utils/constants";
import api from "@/utils/api";

const LandingAuthPage = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot', 'verify-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  // Pre-fill referral code from URL ?ref=XXXX
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setMode('register');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const response = await api.post('/auth/login', { email, password });
        
        // Clear any old cached data first
        localStorage.removeItem('token');
        localStorage.removeItem('userCurrency');
        localStorage.removeItem('userCurrencyTime');
        
        // Set new token
        localStorage.setItem('token', response.data.token);
        toast.success('Welcome back!');
        
        // Force page reload to clear all cached state
        window.location.href = '/';
      } else if (mode === 'register') {
        const payload = { email, password, name, dob };
        if (referralCode.trim()) payload.referral_code = referralCode.trim().toUpperCase();
        const response = await api.post('/auth/register', payload);
        
        // User can login immediately now
        localStorage.setItem('token', response.data.token);
        toast.success(referralCode ? 'Account created! Make your first purchase to claim your $5 credit.' : 'Account created!');
        window.location.href = '/';
      } else if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { email });
        toast.success('If an account exists, a password reset link will be sent to your email.');
        setMode('login');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Authentication failed';
      
      // Check if email verification is needed
      if (error.response?.status === 403 && errorMsg.includes('verify your email')) {
        setVerificationEmail(email);
        setMode('verify-sent');
        toast.error('Please verify your email first.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: verificationEmail });
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      console.log('Starting Google login...');
      const res = await api.get('/auth/google/direct');
      console.log('Google auth response:', res.data);
      if (res.data && res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Failed to connect to Google. Please try again.');
    }
  };

  const handleAppleLogin = async () => {
    try {
      console.log('Starting Apple login...');
      const res = await api.get('/auth/apple');
      console.log('Apple auth response:', res.data);
      if (res.data && res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Apple login error:', error);
      toast.error('Failed to connect to Apple. Please try again.');
    }
  };

  const handleFacebookLogin = async () => {
    try {
      const res = await api.get('/auth/facebook');
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect to Facebook');
    }
  };

  // Email Verification Sent Screen
  if (mode === 'verify-sent') {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Card className="shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Check Your Email</h2>
                <p className="text-slate-600 mb-6">
                  We&apos;ve sent a verification link to<br />
                  <strong>{verificationEmail}</strong>
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  Click the link in the email to verify your account and start using BidZuu.
                </p>
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleResendVerification}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                  <button 
                    className="text-emerald-600 hover:underline text-sm"
                    onClick={() => {
                      setMode('login');
                      setEmail(verificationEmail);
                    }}
                  >
                    Back to Login
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password Screen
  if (mode === 'forgot') {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Card className="shadow-lg">
              <CardContent className="p-6">
                <button 
                  className="flex items-center text-slate-600 hover:text-slate-800 mb-4"
                  onClick={() => setMode('login')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </button>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Forgot Password?</h2>
                <p className="text-slate-600 mb-6">
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-lg font-bold bg-emerald-500 hover:bg-emerald-600"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          
          {/* Left Side - Branding */}
          <div className="flex-1 text-center lg:text-left">
            <img 
              src={LOGO_URL} 
              alt="BidZuu" 
              className="h-24 lg:h-32 mx-auto lg:mx-0 mb-4"
            />
            <h1 className="text-2xl lg:text-3xl text-slate-700 font-medium leading-relaxed">
              BidZuu helps you buy and sell amazing items with your community through auctions and fixed-price listings.
            </h1>
          </div>

          {/* Right Side - Auth Form */}
          <div className="w-full max-w-md">
            <Card className="shadow-lg">
              <CardContent className="p-6">
                {mode === 'register' && (
                  <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3" data-testid="signup-promo-banner">
                    <Gift className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    <p className="text-sm text-emerald-900 leading-tight">
                      <span className="font-semibold">Get $5 OFF your first order</span>
                      {referralCode && <><br /><span className="text-xs">+ $5 referral bonus after your first purchase</span></>}
                    </p>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'register' && (
                    <>
                      <Input
                        placeholder="Full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="h-12 text-base"
                        data-testid="register-name-input"
                      />
                      <div>
                        <Input
                          type="date"
                          placeholder="Date of birth"
                          value={dob}
                          onChange={(e) => setDob(e.target.value)}
                          required
                          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                          className="h-12 text-base"
                        />
                        <p className="text-xs text-muted-foreground mt-1">You must be 18 or older</p>
                      </div>
                    </>
                  )}
                  
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                  
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                  
                  {mode === 'register' && (
                    <div>
                      <Input
                        placeholder="Referral code (optional)"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        maxLength={12}
                        className="h-12 text-base font-mono uppercase"
                        data-testid="register-referral-input"
                      />
                      {referralCode && (
                        <p className="text-xs text-emerald-700 mt-1">You'll get $5 credit after your first purchase</p>
                      )}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-lg font-bold bg-emerald-500 hover:bg-emerald-600"
                    disabled={loading}
                  >
                    {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Sign Up'}
                  </Button>

                  {mode === 'login' && (
                    <button 
                      type="button"
                      className="w-full text-center text-sm text-emerald-600 hover:underline"
                      onClick={() => setMode('forgot')}
                    >
                      Forgot password?
                    </button>
                  )}
                </form>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                {/* Google Login */}
                <Button 
                  variant="outline" 
                  className="w-full h-12 text-base gap-2"
                  onClick={handleGoogleLogin}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                {/* Apple Login */}
                <Button 
                  variant="outline" 
                  className="w-full h-12 text-base gap-2 mt-3"
                  onClick={handleAppleLogin}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  Continue with Apple
                </Button>

                {/* Facebook Login */}
                <Button 
                  variant="outline" 
                  className="w-full h-12 text-base gap-2 mt-3"
                  onClick={handleFacebookLogin}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Continue with Facebook
                </Button>

                <div className="mt-6 text-center">
                  <div className="h-px bg-slate-200 mb-6" />
                  {mode === 'login' ? (
                    <Button 
                      variant="default"
                      className="h-12 px-8 text-base font-bold bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setMode('register')}
                    >
                      Create new account
                    </Button>
                  ) : (
                    <button 
                      className="text-emerald-600 hover:underline font-medium"
                      onClick={() => setMode('login')}
                    >
                      Already have an account? Log In
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Info Text */}
            <p className="text-center text-sm text-muted-foreground mt-4">
              <strong>Create a Page</strong> for a celebrity, brand or business.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            <span>English (US)</span>
            <a href="/terms" className="hover:underline">Terms</a>
            <a href="/privacy" className="hover:underline">Privacy</a>
            <span>© 2026 BidZuu</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingAuthPage;
